import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';

import { DealComparisonActions } from './deal-comparison.actions';
import { Deal } from '../../features/deal/models/deal.model';
import { DealComparisonService } from '../../features/deal/services/deal-comparison.service';
import { DealService } from '../../features/deal/services/deal.service';
import { ActivityEvent } from '../../features/home/models/activity-event.model';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { transactionToDeal } from '../../features/shared/utils/transaction-to-deal';
import { createActivityEffect } from '../create-activity-effect';
import { DealFilterActions } from '../deal-filter/deal-filter.actions';
import { RootState } from '../root-state.model';
import { TransactionSelectors } from '../transaction/transaction.selectors';
import { UserProfileActions } from '../user-profile/user-profile.actions';

@Injectable()
export class DealComparisonEffects {
  private readonly dealService = inject(DealService);
  private readonly comparisonService = inject(DealComparisonService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly actions$ = inject(Actions);

  setDownloadEnabledEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetDownloadEnabled),
      filter((action) => !!action.downloadEnabled),
      map(() => DealComparisonActions.DownloadDeals({}))
    )
  );

  setCompareEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetCompare),
      withLatestFrom(this.store, this.store.select(TransactionSelectors.listLookup)),
      map(([action, store, transactionLookup]) => {
        const dealsWithAccess = action.transactionIds
          .map((tId) => transactionLookup[tId])
          .filter((t) => !!t);

        return DealComparisonActions.SetCompareSuccess({
          deals: dealsWithAccess,
          dealsToDownload: dealsWithAccess.map((d) => d.transactionId),
        });
      })
    )
  );

  addToCompareSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetCompareSuccess),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.dealComparison.downloadEnabled),
      map(([action, store]) => DealComparisonActions.DownloadDeals({}))
    )
  );

  setDealFilterIdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetDealFilterId),
      map((action) => DealFilterActions.UseFilter({ id: action.dealFilterId }))
    )
  );

  downloadDealsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.DownloadDeals),
      withLatestFrom(this.store, this.store.select(TransactionSelectors.listLookup)),
      switchMap(([action, store, transactionLookup]) => {
        const transactionIds = action.transactionIds || store.dealComparison.dealsToDownload;

        const transactionsToDownload = transactionIds
          .map((id) => transactionLookup[id])
          .filter((value) => !!value);

        if (!transactionsToDownload.length) {
          return of(
            DealComparisonActions.DownloadDealsSuccess({ deals: store.dealComparison.deals })
          );
        }

        return forkJoin(
          transactionsToDownload.map((transaction) =>
            this.dealService.getByTransaction(transaction).pipe(
              map((res) => (Object.keys(res).length ? res : transactionToDeal(transaction))),
              catchError(() => of(transactionToDeal(transaction)))
            )
          )
        ).pipe(
          map((deals: Deal[]) =>
            DealComparisonActions.DownloadDealsSuccess({
              deals: [
                ...store.dealComparison.deals.filter(
                  (d) => !transactionIds.includes(d.transactionId)
                ),
                ...deals,
              ],
            })
          ),
          reportEffectError(DealComparisonActions.DownloadDealsFail)
        );
      })
    )
  );

  downloadDealsSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.DownloadDealsSuccess),
      withLatestFrom(this.store),
      map(([action, store]) => DealComparisonActions.Compare())
    )
  );

  compareEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.Compare),
      withLatestFrom(this.store),
      map(([action, store]) =>
        DealComparisonActions.CompareSuccess({
          comparison: this.comparisonService.compareDeals(store.dealComparison.deals as Deal[]),
        })
      )
    )
  );

  toggleBasketEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.ToggleBasket),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const basket = store.dealComparison.basket || [];

        return basket.includes(action.transactionId)
          ? DealComparisonActions.RemoveFromBasket({ transactionId: action.transactionId })
          : DealComparisonActions.AddToBasket({ transactionIds: [action.transactionId] });
      })
    )
  );

  syncBasketEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DealComparisonActions.AddToBasket,
        DealComparisonActions.RemoveFromBasket,
        DealComparisonActions.ClearBasket
      ),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const basket = store.dealComparison.basket || [];

        return UserProfileActions.UpdateAssetSetting({
          setting: {
            basket,
          },
        });
      })
    )
  );

  refreshBasketEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.RefreshAssetSettingSuccess),
      withLatestFrom(this.store),
      filter(
        ([action, store]) =>
          !isEqual(action.assetSetting?.basket || [], store.dealComparison.basket)
      ),
      map(([action, store]) =>
        DealComparisonActions.SetBasket({ basket: action.assetSetting?.basket || [] })
      )
    )
  );

  addToBasket$ = createActivityEffect(
    this.actions$,
    DealComparisonActions.AddToBasket,
    ActivityEvent.SelectedDealsAdd
  );
  removeFromBasket$ = createActivityEffect(
    this.actions$,
    DealComparisonActions.RemoveFromBasket,
    ActivityEvent.SelectedDealsRemove
  );
  clearBasket$ = createActivityEffect(
    this.actions$,
    DealComparisonActions.ClearBasket,
    ActivityEvent.SelectedDealsRemove
  );
}
